/**
 * A preset is a string representing a known app environment the player will be used in.
 *
 * @remarks
 * The player uses the preset to configure itself for the specific app environment.
 * This includes which plugins to load, default values like the player type, but also the app identifier for the remote config.
 *
 * A preset must be provided for large scale implementations,
 * because the preset is used to identify the app in the NPAW Analytics, if it is not set explicitly,
 * and it is also used to identify the app in the remote config.
 *
 * Without a valid preset analytics data will not be attached to the correct account and the remote config will not work.
 * Both a crucial for the player to work correctly and thus a smooth playback experience.
 *
 * @deprecated - Please use {@link Client} and {@link Scope} instead.
 */
export const Preset = {
  /**
   * Default Preset for media playback without advertising
   *
   * @remarks
   * If the preset is not set explicitly, the player will use this one.
   *
   * This is a great starting point for most apps when they start using the player.
   * If you know that you will play ads, you should use the `DefaultAdvertising` preset instead.
   *
   * The player will not throw an error if you pass an advertising config,
   * but it will not play any ads, because there is no component reacting to the advertising config.
   */
  Default: 'default',

  /**
   * Default Preset for media playback with advertising functionality.
   *
   * @remarks
   * This preset will enable the advertising plugin and configure it with default values.
   * This does not automatically mean that ads will be played,
   * but it will be possible to play ads with a valid advertising config.
   */
  DefaultAdvertising: 'default-advertising',

  /**
   * Default Preset for audio playback with advertising functionality.
   *
   * @remarks
   * This preset will optimize the player for progressive audio content.
   * It will not be possible to play DASH or HLS streams with this preset.
   *
   * This preset will enable the advertising plugin and configure it with default values.
   * This does not automatically mean that ads will be played,
   * but it will be possible to play ads with a valid advertising config.
   */
  Audio: 'audio',

  /**
   * Preset to just use our native player for advertising (and short progressive content).
   *
   * @remarks
   * This preset will enable the advertising plugin, but will instead not enable most of the other plugins.
   *
   * Use this preset if you want to show ads before HTML5 games or others forms of content like an online contest.
   *
   * This present cannot be used to play long form content.
   */
  AdPlayer: 'ad-player',

  /**
   * General preset for all G+J products
   *
   * @remarks
   * This present is similar to the `DefaultAdvertising` preset.
   * The main difference is that we reserve this preset to enable some features via our remote config.
   *
   * We currently do not change any significant values in the remote config, but we may do so in the future.
   */
  GAndJ: 'G+J',

  /**
   * Preset for RTL+ at tvnow.de
   *
   * @deprecated
   *
   * @remarks
   * This preset should not be used anymore, because the product TVNOW is superseeded by RTL+.
   *
   * This preset previously enabled our legacy plugins to transform the supplied config,
   * from a format that is compatible with the CBC-Player, to the format that is compatible with the Foundation Player.
   *
   * If you are migrating from the CBC-Player to the Foundation Player, please contact the Foundation Player team first.
   */
  TVNOW: 'tvnow',

  /**
   * Preset for RTL+ MPA Watch at plus.rtl.de
   *
   * @remarks
   *
   * This preset should be used for the WATCH section of RTL+.
   * It enables advertising, long form content, analytics and all long content features.
   *
   * Most of the features are enabled via the remote config and can be toggled off in case of an emergency.
   *
   * Some features may be available in the player and are not active.
   * These may be enabled via the remote config in the future, depending on the needs of the player and RTL+.
   */
  RTLPlusWatch: 'rtlplusmpawatch',

  /**
   * @deprecated Use RTLPlusRadio instead
   */
  RTLPlusMusicRadio: 'rtlplusmusikradio',

  /**
   * @deprecated Use RTLPlusAudiobook instead
   */
  RTLPlusMusicAudiobook: 'rtlplusmusikaudiobook',

  /**
   * @deprecated Use RTLPlusPodcast instead
   */
  RTLPlusMusicPodcast: 'rtlplusmusikpodcast',

  /**
   * Preset for RTL+ MPA Radio at plus.rtl.de
   *
   * @remarks
   * This preset should be used for the radio streams in the MUSIC section of RTL+.
   *
   * This will optimize the player for progressive content and disable most of the features that are not needed for radio.
   *
   * This will use our native player for playback, because it is optimized for progressive content and the most lightweight player.
   */
  RTLPlusRadio: 'rtlplusmparadio',

  /**
   * Preset for RTL+ MPA Audiobook at plus.rtl.de
   *
   * @remarks
   * This preset should be used for the audiobook streams in the AUDIOBOOKS section of RTL+.
   *
   * This preset is very similar to the `RTLPlusWatch` preset,
   * because audiobooks are very similar from a technical perspective,
   * because they also use DASH and HLS streams.
   *
   * The main difference is that this preset may have a different remote config.
   */
  RTLPlusAudiobook: 'rtlplusmpaaudiobook',

  /**
   * Preset for RTL+ MPA Podcast at plus.rtl.de
   *
   * @remarks
   * This preset should be used for the podcast streams in the PODCASTS section of RTL+.
   *
   * This preset is very similar to the `RTLPlusRadio` preset,
   * because podcasts are very similar from a technical perspective,
   * because they also use progressive streams.
   *
   * The main difference is that this preset will disable Advertising and may have a different remote config.
   */
  RTLPlusPodcast: 'rtlplusmpapodcast',

  /**
   * Preset for SuperRTL Toggo at toggo.de
   *
   * @remarks
   * This preset should be used for toggo.de.
   *
   * This preset is very similar to the `RTLPlusWatch` preset,
   * because both apps offer long form content with advertisements to their users,
   * but toggo.de does not need as many features as RTL+.
   *
   * Notable exceptions for now is the lack of CDN balancing and some tracking plugins.
   */
  SuperRTLToggo: 'superrtltoggo',

  /**
   * Preset for RTL Media Hub
   *
   * Advertisement is always disabled.
   */
  MediaHub: 'mediahub',

  /**
   * Preset for NTV
   */
  NTV: 'ntv',
} as const;

/**
 * {@inheritDoc Preset:var}
 */
export type Preset = (typeof Preset)[keyof typeof Preset];
